import * as React from 'react';

import { faSpinnerThird } from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';
import * as z from 'zod';

import { useCreateParkingAPIConfigs } from 'src/features/parkingManagement/api/useCreateParkingAPIConfigs.ts';
import { type ParkingAPIConfig } from 'src/features/parkingManagement/api/useParkingAPIConfigs';
import { useUpdateParkingAPIConfigs } from 'src/features/parkingManagement/api/useUpdateParkingAPIConfigs';
import { parkingManagementMessages } from 'src/features/parkingManagement/utils';
import {
    FormFieldBlock,
    FormFieldBlockBody,
    FormFieldBlockLabel,
    FormFieldBlockName,
    FormFieldBlockSeparator,
    FormFieldBlockSubtitle,
    FormFieldBlockTitle,
} from 'src/registry/components/blocks/FormFieldBlock';
import { useToast } from 'src/registry/hooks/UseToast';
import { Button } from 'src/registry/ui/Button';
import { Form, FormControl, FormField, FormItem, FormMessage } from 'src/registry/ui/Form';
import { Input } from 'src/registry/ui/Input';
import { generalErrorMessages, generalMessages } from 'src/utils/messages';

type IntegrationsFormProps = {
    data?: ParkingAPIConfig;
    refetch: () => void;
};

type FormValues = z.infer<typeof formSchema>;
type FormFieldName = keyof FormValues;

const formSchema = z.object({
    parkingUUID: z.string().min(1),
    endpoint: z.string().min(1),
    api_key: z.string().min(1),
    media_endpoint: z.string().min(1),
    image_prefix: z.string().min(1),
    admin_user_id: z.string().min(1),
    cameras_stream_username: z.string().optional(),
    cameras_stream_password: z.string().optional(),
});

const IntegrationsForm = ({ data, refetch }: IntegrationsFormProps) => {
    const { formatMessage } = useIntl();
    const { parkingId } = useParams();
    const { toast } = useToast();

    const defaultValues = {
        parkingUUID: parkingId || '',
        endpoint: data?.endpoint || '',
        api_key: data?.api_key || '',
        media_endpoint: data?.media_endpoint || '',
        image_prefix: data?.image_prefix || '',
        admin_user_id: data?.admin_user_id || '',
        cameras_stream_username: data?.cameras_stream_username,
        cameras_stream_password: data?.cameras_stream_password,
    };

    React.useEffect(() => {
        if (!data) return;
        form.reset(defaultValues);
        return () => {
            form.reset();
        };
    }, [data]);

    const form = useForm<z.infer<typeof formSchema>>({
        resolver: zodResolver(formSchema),
        mode: 'onChange',
        defaultValues,
    });

    const {
        formState: { isDirty },
    } = form;

    const { mutate: createParkingConfigs, isPending: isPendingCreate } = useCreateParkingAPIConfigs({
        onSuccess: () => {
            refetch();
            toast({
                title: formatMessage(parkingManagementMessages.parkingChangeSuccess),
            });
        },
        onError: (error) => {
            const errorData = error.response?.data;
            const errorCode = errorData?.code ?? formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = errorData?.message ?? formatMessage(generalErrorMessages.unknownError);

            toast({
                title: errorCode,
                description: errorMessage,
                variant: 'destructive',
            });
        },
    });

    const { mutate: updateParkingConfigs, isPending: isPendingUpdate } = useUpdateParkingAPIConfigs({
        onSuccess: () => {
            refetch();
            toast({
                title: formatMessage(parkingManagementMessages.parkingChangeSuccess),
            });
        },
        onError: (error) => {
            const errorData = error.response?.data;
            const errorCode = errorData?.code ?? formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = errorData?.message ?? formatMessage(generalErrorMessages.unknownError);

            toast({
                title: errorCode,
                description: errorMessage,
                variant: 'destructive',
            });
        },
    });

    const onSubmit: SubmitHandler<z.infer<typeof formSchema>> = (values: z.infer<typeof formSchema>) => {
        if (!data) {
            createParkingConfigs({
                parkingId,
                ...values,
            });
        } else
            updateParkingConfigs({
                parkingId,
                id: data.id,
                ...values,
            });
    };

    const getFormFieldBlock = (
        formFieldName: FormFieldName,
        formFieldBlockName: { id: string; defaultMessage: string },
    ) => {
        return (
            <FormFieldBlock>
                <FormFieldBlockLabel>
                    <FormFieldBlockTitle>
                        <FormFieldBlockName>{formatMessage(formFieldBlockName)}</FormFieldBlockName>
                    </FormFieldBlockTitle>
                </FormFieldBlockLabel>
                <FormFieldBlockBody>
                    <FormField
                        control={form.control}
                        name={formFieldName}
                        render={({ field }) => (
                            <FormItem className="w-full">
                                <FormControl>
                                    <Input type="text" {...field} value={field.value ?? ''} />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                </FormFieldBlockBody>
            </FormFieldBlock>
        );
    };

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)}>
            <FormFieldBlockTitle className="font-bold">
                {formatMessage(parkingManagementMessages.generalInformation)}
            </FormFieldBlockTitle>
            <FormFieldBlockSeparator />
            <FormFieldBlock>
                <FormFieldBlockLabel>
                    <FormFieldBlockTitle>
                        <FormFieldBlockName className="truncate">
                            {formatMessage(parkingManagementMessages.parkingUUID)}
                        </FormFieldBlockName>
                    </FormFieldBlockTitle>
                    <FormFieldBlockSubtitle>
                        {formatMessage(parkingManagementMessages.parkingUUIDDescription)}
                    </FormFieldBlockSubtitle>
                </FormFieldBlockLabel>
                <FormFieldBlockBody>
                    <FormField
                        control={form.control}
                        name="parkingUUID"
                        render={({ field }) => (
                            <FormItem className="w-full">
                                <FormControl>
                                    <Input type="text" readOnly disabled {...field} />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                </FormFieldBlockBody>
            </FormFieldBlock>
            <FormFieldBlockSeparator />
            {getFormFieldBlock('endpoint', parkingManagementMessages.apiEndpoint)}
            <FormFieldBlockSeparator />
            {getFormFieldBlock('api_key', parkingManagementMessages.apiKey)}
            <FormFieldBlockSeparator />
            {getFormFieldBlock('media_endpoint', parkingManagementMessages.mediaEndpoint)}
            <FormFieldBlockSeparator />
            {getFormFieldBlock('image_prefix', parkingManagementMessages.photosPrefix)}
            <FormFieldBlockSeparator />
            {getFormFieldBlock('admin_user_id', parkingManagementMessages.adminUserId)}
            <FormFieldBlockSeparator />
            {getFormFieldBlock('cameras_stream_username', parkingManagementMessages.cameraStreamUsername)}
            <FormFieldBlockSeparator />
            {getFormFieldBlock('cameras_stream_password', parkingManagementMessages.cameraStreamPassword)}
            <FormFieldBlockSeparator />

            <div className="flex justify-end">
                <Button className="w-full sm:w-auto" type="submit" disabled={!isDirty}>
                    {(isPendingUpdate || isPendingCreate) && (
                        <FontAwesomeIcon icon={faSpinnerThird} className="animate-spin text-lg" />
                    )}
                    {formatMessage(generalMessages.saveChanges)}
                </Button>
            </div>
        </Form>
    );
};

export default IntegrationsForm;
